import { Fragment, useEffect, useState } from 'react'
import { AppProps } from 'next/app'
import type { NextWebVitalsMetric } from 'next/app'
import { StyleSheetManager, ThemeProvider } from 'styled-components'
import { queries, ScreenProvider, shouldForwardProp, theme } from 'react-components'
import { polyfill } from 'smoothscroll-polyfill'
import Head from 'next/head'
import Script from 'next/script'
import { GoogleTagManager } from '@next/third-parties/google'

// Must import before GlobalStyles — styled-components orders global styles by import order.
import GlobalNormalize from '../components/common/global-normalize'
import { GlobalFontFaces } from '../common/fonts'
import { getDefaultValuesForDevice, isServer } from '../utils/platform-utils'
import { PageProps } from '../common/types'
import config from '../common/config'
import ReduxProvider from '../redux/provider'
import UserSettingsConfigProvider from '../provider/user-settings-config'
import GlobalStyles from '../components/common/global-styles'
import { useDateFormatter } from '../hooks/date'
import firebaseConfig, { performanceStandaloneUrl } from '../common/firebase'
import { useScript } from '../hooks/dom'
import useWebVitals from '../hooks/web-vitals'
import { ABConfigProvider } from '../context/ab-config-context'
import { SiteUrlProvider, SiteUrlContextValue } from '../context/site-url'

const initializeFirebase = () => {
    if ((globalThis as any).firebase && firebaseConfig.apiKey) {
        // Initialize firebase with Firebase App config.
        ;(globalThis as any).firebase.initializeApp(firebaseConfig).performance()
    }
}

// Lives here (not page.tsx) so it always runs, even on pages whose own content renders null.
const _consentDefaultHtml = {
    __html: `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}

        if (localStorage.getItem('consentMode') === null) {
            gtag('consent', 'default', {
                'ad_storage': 'denied',
                'ad_user_data': 'denied',
                'ad_personalization': 'denied',
                'analytics_storage': 'denied',
                'functionality_storage': 'denied',
                'personalization_storage': 'denied',
                'security_storage': 'denied',
            });
        } else {
            gtag('consent', 'default', JSON.parse(localStorage.getItem('consentMode')));
        }
    `,
}

interface OwnProps {
    // Workaround for https://github.com/zeit/next.js/issues/8592.
    err?: any
}

interface Props extends OwnProps, AppProps {
    pageProps: PageProps
}

export function reportWebVitals({ id, name, label, value }: NextWebVitalsMetric) {
    if (
        globalThis.window !== undefined &&
        (globalThis as any).dataLayer?.push &&
        !!document.getElementById('region-landing-page-main') &&
        label === 'web-vital' &&
        (name === 'LCP' || name === 'CLS' || name === 'INP')
    ) {
        ;(globalThis as any).dataLayer.push({
            event: 'web-vitals',
            event_category: 'Web Vitals',
            event_action: name,
            // Google Analytics metrics must be integers, so the value is rounded.
            // For CLS the value is first multiplied by 1000 for greater precision
            // (note: increase the multiplier for greater precision if needed).
            event_value: Math.round(name === 'CLS' ? value * 1000 : value),
            // The 'id' value will be unique to the current page load. When sending
            // multiple values from the same page (e.g. for CLS), Google Analytics can
            // compute a total by grouping on this ID (note: requires `eventLabel` to
            // be a dimension in your report).
            event_label: id,
        })
    }
}

const App = ({ Component, pageProps, err }: Props) => {
    const [isClient, setIsClient] = useState(false)

    useWebVitals()

    useEffect(() => {
        polyfill()
        setIsClient(!isServer)
    }, [])

    const {
        device,
        initialReduxState,
        requestUserConfig,
        messages,
        routes,
        originalPath,
        originalQuery,
        originalUrl,
        ...restPageProps
    } = pageProps

    const siteUrl: SiteUrlContextValue = {
        originalPath: originalPath ?? '',
        originalQuery: originalQuery ?? {},
        originalUrl: originalUrl ?? '',
    }

    // Call at the top-level so that formatter method is defined.
    useDateFormatter()

    useScript(performanceStandaloneUrl, { defer: true }, initializeFirebase)

    return (
        <Fragment>
            <Head>
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
            </Head>
            <Script id="consent-default" strategy="beforeInteractive" dangerouslySetInnerHTML={_consentDefaultHtml} />
            <GoogleTagManager gtmId={config.gtmContainerId} />
            {}
            {/* @ts-expect-error -- react-components types are not yet React 19 compatible */}
            <ScreenProvider queries={queries} values={isClient ? undefined : getDefaultValuesForDevice(device)}>
                <StyleSheetManager shouldForwardProp={shouldForwardProp}>
                    <ThemeProvider theme={theme}>
                        <UserSettingsConfigProvider
                            requestUserConfig={requestUserConfig}
                            messages={messages}
                            routes={routes}
                        >
                            <SiteUrlProvider value={siteUrl}>
                                <GlobalNormalize />
                                <GlobalFontFaces />
                                <GlobalStyles />
                                <ABConfigProvider>
                                    <ReduxProvider initialState={initialReduxState}>
                                        <Component {...restPageProps} err={err} />
                                    </ReduxProvider>
                                </ABConfigProvider>
                            </SiteUrlProvider>
                        </UserSettingsConfigProvider>
                    </ThemeProvider>
                </StyleSheetManager>
            </ScreenProvider>
        </Fragment>
    )
}

export default App
